// Client-side wrappers for recalibration Cloud Functions.
// Keeps the callable names in one place so UI components don't hardcode them.
//
// Source: CogniPrint_Recalibration_Dev_Plan_v1.md §3.1 (REC-010/011).

import {
  collection,
  getFirestore,
  doc,
  onSnapshot,
  orderBy,
  query,
  where,
  type Unsubscribe,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from './firebase';
import type {
  Day14Offer,
  RecalibrationPreference,
  RecalibrationSchedule,
} from '../types/recalibration';

function functions() {
  return getFunctions(app);
}

export async function ensureRecalibrationSchedule(): Promise<{
  schedule: RecalibrationSchedule;
}> {
  const fn = httpsCallable<unknown, { schedule: RecalibrationSchedule }>(
    functions(),
    'ensureRecalibrationSchedule',
  );
  const { data } = await fn({});
  return data;
}

export async function requestAdHocRecalibration(): Promise<{
  allowed: boolean;
  tier: string;
  hasCoachAddOn: boolean;
  cooldownDays: number;
}> {
  const fn = httpsCallable(functions(), 'requestAdHocRecalibration');
  const { data } = await fn({});
  return data as {
    allowed: boolean;
    tier: string;
    hasCoachAddOn: boolean;
    cooldownDays: number;
  };
}

export async function updateRecalibrationPreference(
  preference: RecalibrationPreference,
): Promise<{
  preference: RecalibrationPreference;
  schedule: RecalibrationSchedule;
  cycleDays: number;
}> {
  const fn = httpsCallable(functions(), 'updateRecalibrationPreference');
  const { data } = await fn({ preference });
  return data as {
    preference: RecalibrationPreference;
    schedule: RecalibrationSchedule;
    cycleDays: number;
  };
}

export async function completeRecalibration(input: {
  sessionId: string;
  type: 'check' | 'full' | 'micro';
  softReset?: boolean;
}): Promise<{
  schedule: RecalibrationSchedule;
  cycleDays: number;
}> {
  const fn = httpsCallable(functions(), 'completeRecalibration');
  const { data } = await fn(input);
  return data as { schedule: RecalibrationSchedule; cycleDays: number };
}

// ─── Day-14 Confidence Preview + Offer (Free tier) ───

export interface PreviewQuestion {
  id: string;
  text: string;
  chips: string[];
  allow_other: boolean;
}

export interface PreviewStartResult {
  status: 'ready' | 'preview_already_completed';
  sessionId?: string;
  weakestDimension?: { key: string; displayName: string };
  questions?: PreviewQuestion[];
  offer?: Day14Offer;
}

export async function startConfidencePreview(): Promise<PreviewStartResult> {
  const fn = httpsCallable(functions(), 'startConfidencePreview');
  const { data } = await fn({});
  return data as PreviewStartResult;
}

export interface PreviewAnswer {
  questionId: string;
  chipSelections: string[];
  freeText: string | null;
}

export async function completeConfidencePreview(args: {
  sessionId: string;
  answers: PreviewAnswer[];
}): Promise<{ status: 'completed'; headline: string; offer: Day14Offer }> {
  const fn = httpsCallable(functions(), 'completeConfidencePreview');
  const { data } = await fn(args);
  return data as { status: 'completed'; headline: string; offer: Day14Offer };
}

// ─── Day-14 Confidence Check (Learner+) ───

export interface CheckQuestion {
  id: string;
  dimension: string;
  text: string;
  chips: string[];
  allow_other: boolean;
}

export interface CheckSelfAssessment {
  profile_summary: string;
  prompt: string;
}

export interface CheckStartResult {
  sessionId: string;
  questions: CheckQuestion[];
  selfAssessment: CheckSelfAssessment;
}

export async function startConfidenceCheck(): Promise<CheckStartResult> {
  const fn = httpsCallable(functions(), 'startConfidenceCheck');
  const { data } = await fn({});
  return data as CheckStartResult;
}

export interface CheckAnswer {
  questionId: string;
  chipSelections: string[];
  freeText: string | null;
}

export interface DimensionChange {
  dimension: string;
  dimensionDisplayName: string;
  previousScore: number;
  newScore: number;
  confidenceBefore: number;
  confidenceAfter: number;
  reasoning: string;
}

export async function completeConfidenceCheck(args: {
  sessionId: string;
  answers: CheckAnswer[];
  selfAssessmentResponse: string | null;
}): Promise<{
  status: 'completed';
  overallSummary: string;
  dimensionChanges: DimensionChange[];
}> {
  const fn = httpsCallable(functions(), 'completeConfidenceCheck');
  const { data } = await fn(args);
  return data as {
    status: 'completed';
    overallSummary: string;
    dimensionChanges: DimensionChange[];
  };
}

// ─── Full recalibration (Learner/Pro/Teams, 4-week cadence) ───

export interface FullReflectionQuestion {
  id: string;
  text: string;
  chips: string[];
  allow_other: boolean;
}

export interface FullDimensionQuestion {
  id: string;
  dimension: string;
  current_score: number;
  current_label: string;
  text: string;
  chips: string[];
  allow_other: boolean;
}

export interface FullPlan {
  reflection: FullReflectionQuestion;
  dimension_confirmations: FullDimensionQuestion[];
  accuracy_check: FullReflectionQuestion;
  suggestion: FullReflectionQuestion;
}

export async function startFullRecalibration(): Promise<{
  sessionId: string;
  plan: FullPlan;
}> {
  const fn = httpsCallable(functions(), 'startFullRecalibration');
  const { data } = await fn({});
  return data as { sessionId: string; plan: FullPlan };
}

export interface FullCompleteInput {
  sessionId: string;
  reflection: { chipSelections: string[]; freeText: string | null };
  dimensionConfirmations: Array<{
    dimension: string;
    userResponse: string;
    freeText: string | null;
  }>;
  accuracyCheck: { chipSelections: string[]; freeText: string | null };
  suggestion: { freeText: string | null };
  softReset?: boolean;
}

export async function completeFullRecalibration(input: FullCompleteInput): Promise<{
  status: 'completed';
  overallSummary: string;
  dimensionChanges: DimensionChange[];
  cycleDays: number;
}> {
  const fn = httpsCallable(functions(), 'completeFullRecalibration');
  const { data } = await fn(input);
  return data as {
    status: 'completed';
    overallSummary: string;
    dimensionChanges: DimensionChange[];
    cycleDays: number;
  };
}

// ─── Monthly report (REC-040) ───

export interface MonthlyReport {
  userId: string;
  yearMonth: string;
  generatedAt: string;
  subjectLine: string;
  lastMonthLoopClose: string | null;
  experiment: {
    title: string;
    description: string;
    measurableSignal: string;
  };
  nextUp: Array<{ title: string; rationale: string }>;
  profileEvolutionPreview: string;
  reflectiveQuestion: string;
  openedAt: string | null;
  reflectiveAnswer: string | null;
  reflectiveAnsweredAt: string | null;
}

export async function listMonthlyReports(
  userId: string,
  limit = 12,
): Promise<MonthlyReport[]> {
  const db = getFirestore(app);
  const { collection, query, orderBy, limit: fbLimit, getDocs } = await import(
    'firebase/firestore'
  );
  const q = query(
    collection(db, 'monthlyReports', userId, 'reports'),
    orderBy('generatedAt', 'desc'),
    fbLimit(limit),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => d.data() as MonthlyReport);
}

export async function markMonthlyReportOpened(
  userId: string,
  yearMonth: string,
): Promise<void> {
  const db = getFirestore(app);
  const { doc: docRef, updateDoc } = await import('firebase/firestore');
  await updateDoc(docRef(db, 'monthlyReports', userId, 'reports', yearMonth), {
    openedAt: new Date().toISOString(),
  });
}

export async function submitReflectiveAnswer(args: {
  yearMonth: string;
  answer: string;
}): Promise<{ status: 'classified' }> {
  const fn = httpsCallable(functions(), 'submitReflectiveAnswer');
  const { data } = await fn(args);
  return data as { status: 'classified' };
}

export function subscribeToDay14Offer(
  userId: string,
  onUpdate: (offer: Day14Offer | null) => void,
): Unsubscribe {
  const db = getFirestore(app);
  const ref = doc(db, 'day14Offers', userId);
  return onSnapshot(ref, (snap) => {
    onUpdate(snap.exists() ? (snap.data() as Day14Offer) : null);
  });
}

// ─── Coach-only surfaces (REC-072, REC-073) ───

export async function previewCounterfactual(args: {
  dimension: string;
  currentLabel: string;
  alternativeLabel: string;
}): Promise<{ sentence: string }> {
  const fn = httpsCallable(functions(), 'previewCounterfactual');
  const { data } = await fn(args);
  return data as { sentence: string };
}

export interface AutoPilotSuggestion {
  suggestionId: string;
  userId: string;
  dimension: string;
  dimensionDisplayName: string;
  direction: 'up' | 'down';
  magnitude: number;
  cardSentence: string;
  rationale: string;
  status: 'pending' | 'accepted' | 'dismissed';
  createdAt: string;
  resolvedAt: string | null;
}

export async function resolveAutoPilotSuggestion(args: {
  suggestionId: string;
  decision: 'accept' | 'dismiss';
}): Promise<{ ok: true; applied?: boolean; newScore?: number; alreadyResolved?: boolean }> {
  const fn = httpsCallable(functions(), 'resolveAutoPilotSuggestion');
  const { data } = await fn(args);
  return data as {
    ok: true;
    applied?: boolean;
    newScore?: number;
    alreadyResolved?: boolean;
  };
}

export function subscribeToAutoPilotSuggestions(
  userId: string,
  onUpdate: (suggestions: AutoPilotSuggestion[]) => void,
): Unsubscribe {
  const db = getFirestore(app);
  const q = query(
    collection(db, 'autoPilotSuggestions'),
    where('userId', '==', userId),
    where('status', '==', 'pending'),
    orderBy('createdAt', 'desc'),
  );
  return onSnapshot(q, (snap) => {
    onUpdate(snap.docs.map((d) => d.data() as AutoPilotSuggestion));
  });
}

export function subscribeToRecalibrationSchedule(
  userId: string,
  onUpdate: (schedule: RecalibrationSchedule | null) => void,
): Unsubscribe {
  const db = getFirestore(app);
  const ref = doc(db, 'recalibrationSchedules', userId);
  return onSnapshot(ref, (snap) => {
    onUpdate(
      snap.exists() ? (snap.data() as RecalibrationSchedule) : null,
    );
  });
}
